import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { transformImage, generateProducts, getTemplates, fetchProductPrice, consumeCredit, getUserOrders } from '../services/api';
import type { GeneratedProduct, TemplateConfig } from '../services/api';
import { Upload, Wand2, ShoppingBag, CreditCard, Sparkles, Menu, X, Loader2 } from 'lucide-react';
import { ref, listAll, getDownloadURL, uploadString, uploadBytes } from 'firebase/storage';
import type { StorageReference } from 'firebase/storage';
import { doc, getDoc, setDoc, collection, getDocs, onSnapshot } from 'firebase/firestore';
import { storage, db } from '../services/firebase';

import type { Creation, Genre, Product, StyleImage, GelatoProduct } from '../types/create';

import { CreationSidebar } from '../components/create/CreationSidebar';
import { StageIndicator } from '../components/create/StageIndicator';
import { UploadImage } from '../components/create/UploadImage';
import { StyleSelect } from '../components/create/StyleSelect';
import { TransformationSuccess } from '../components/create/TransformationSuccess';
import { CustomizedProducts } from '../components/create/CustomizedProducts';
import { OrderDetails } from '../components/create/OrderDetails';
import { BuyCreditsModal } from '../components/BuyCreditsModal';
import { SEO } from '../components/SEO';

export const Create = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [creations, setCreations] = useState<Creation[]>([]);
  const [selectedCreationId, setSelectedCreationId] = useState<string | null>(null);
  const [currentStage, setCurrentStage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allCreationIds, setAllCreationIds] = useState<string[]>([]);
  const [loadedCount, setLoadedCount] = useState(0);
  const [fetchedCreationIds, setFetchedCreationIds] = useState<Set<string>>(new Set());
  const BATCH_SIZE = 10;
  
  // New Creation State
  const [uploadedImagePreview, setUploadedImagePreview] = useState<string | null>(null);
  const [currentTransformedImage, setCurrentTransformedImage] = useState<string | null>(null);
  const [currentProducts, setCurrentProducts] = useState<GeneratedProduct[]>([]);
  const [isGeneratingProducts, setIsGeneratingProducts] = useState(false);
  
  // Style Selection State
  const [genres, setGenres] = useState<Genre[]>([]);
  const [selectedGenre, setSelectedGenre] = useState<Genre | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<StyleImage | null>(null);
  const [loadingStyles, setLoadingStyles] = useState(false);
  const [isBrowsingStyles, setIsBrowsingStyles] = useState(false);
  const [isTransforming, setIsTransforming] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | GeneratedProduct | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [templateConfig, setTemplateConfig] = useState<TemplateConfig | null>(null);
  const [loadingSecondaryTemplates, setLoadingSecondaryTemplates] = useState<string[]>([]);
  const [longGenerationModalOpen, setLongGenerationModalOpen] = useState(false);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [currentCreationId, setCurrentCreationId] = useState<string | null>(null);
  const [latestOrderPreview, setLatestOrderPreview] = useState<string | null>(null);
  const pollingUnsubscribeRef = useRef<(() => void) | null>(null);
  const pollingTimeoutRef = useRef<any>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingUnsubscribeRef.current) {
        pollingUnsubscribeRef.current();
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);
  
  // Stages configuration
  const stages = [
    {
      id: 1,
      title: "Uploaded Image",
      description: "Upload your photo to begin the transformation.",
      icon: Upload,
      image: "/assets/process/leftUp.jpg",
      action: "Upload"
    },
    {
      id: 2,
      title: "Selected Style",
      description: "Choose an artistic style for your image.",
      icon: Wand2,
      image: "/assets/process/rightUp.png",
      action: "Style"
    },
    {
      id: 3,
      title: "Transformed Image",
      description: "Watch your image come to life.",
      icon: Sparkles,
      image: "/assets/process/transformed_placeholder.jpg",
      action: "Transform"
    },
    {
      id: 4,
      title: "Customized Product",
      description: "Preview your art on high-quality apparel.",
      icon: ShoppingBag,
      image: "/assets/process/leftBottom.jpeg",
      action: "Preview"
    },
    {
      id: 5,
      title: "Order Details",
      description: "Order your custom masterpiece.",
      icon: CreditCard,
      image: "/assets/process/rightBottom.png",
      action: "Order"
    }
  ];

  useEffect(() => {
    if (user) {
      fetchCreations();
    }
    // Fetch templates once
    getTemplates().then(config => {
      setTemplateConfig(config);
    }).catch(err => console.error("Failed to load templates", err));
    
    // Check for return from Order or restore state
    if (location.state?.creationId) {
        if (location.state.openOrderDetails) {
            setSelectedCreationId(location.state.creationId);
            setCurrentStage(5);
            setMobileSidebarOpen(false);
        } else if (location.state.restoreStage) {
            setSelectedCreationId(location.state.creationId);
            setCurrentCreationId(location.state.creationId);
            setCurrentStage(location.state.restoreStage);
            setMobileSidebarOpen(false);
        }
    }
  }, [user]);

  // Check for existing orders when a creation is selected
  useEffect(() => {
    if (!selectedCreationId || !user) {
        setLatestOrderPreview(null);
        return;
    }

    const checkOrders = async () => {
        try {
            const orders = await getUserOrders(false);
            const creationOrders = orders.filter(order => {
                const creationIdMeta = order.metadata?.find(m => m.key === 'creation_id' || m.key === 'creation_hash');
                return creationIdMeta && creationIdMeta.value === selectedCreationId;
            });

            if (creationOrders.length > 0) {
                // Sort by date descending
                creationOrders.sort((a, b) => {
                    const dateA = a.createdAtServer ? new Date(a.createdAtServer).getTime() : 0;
                    const dateB = b.createdAtServer ? new Date(b.createdAtServer).getTime() : 0;
                    return dateB - dateA;
                });

                const latestOrder = creationOrders[0];
                if (latestOrder.items && latestOrder.items.length > 0) {
                    const firstItem = latestOrder.items[0];
                    if (firstItem.files && firstItem.files.length > 0) {
                        setLatestOrderPreview(firstItem.files[0].url);
                    }
                }
            } else {
                setLatestOrderPreview(null);
            }
        } catch (error) {
            console.error("Failed to fetch orders for preview", error);
            setLatestOrderPreview(null);
        }
    };

    checkOrders();
  }, [selectedCreationId, user]);

  // Trigger product loading if selected creation has no products loaded yet
  useEffect(() => {
    if (!selectedCreationId || !user || creations.length === 0) return;
    
    const creation = creations.find(c => c.id === selectedCreationId);
    
    if (creation && creation.products.length === 0 && !fetchedCreationIds.has(selectedCreationId) && !loadingProducts) {
        loadCreationProducts(selectedCreationId);
    }
  }, [selectedCreationId, creations, user, fetchedCreationIds, loadingProducts]);

  // Ensure products are fetched when entering Stage 4 (Customized Products)
  useEffect(() => {
    if (currentStage === 4 && user) {
        const creationId = selectedCreationId || currentCreationId;
        
        if (creationId) {
             const creation = creations.find(c => c.id === creationId);
             
             // If creation is not loaded yet (e.g. initial fetch pending), wait for it.
             // loadCreationProducts requires the creation to be in the state to update it.
             if (!creation) return;

             // If we have no products in memory for this creation
             const hasProducts = (creation.products.length > 0) || currentProducts.length > 0;
             
             // If we haven't fetched OR we have 0 products (and want to retry fetching in case they appeared)
             // We can be a bit more aggressive here than the other useEffect
             if (!hasProducts && !loadingProducts && !isGeneratingProducts && !fetchedCreationIds.has(creationId)) {
                 loadCreationProducts(creationId);
             }
        }
    }
  }, [currentStage, selectedCreationId, currentCreationId, user, creations, currentProducts.length, loadingProducts, isGeneratingProducts, fetchedCreationIds]);

  const fetchCreationDetails = async (creationHash: string, userUid: string): Promise<Creation | null> => {
      const year = parseInt(creationHash.substring(0, 4));
      const month = parseInt(creationHash.substring(4, 6)) - 1; 
      const day = parseInt(creationHash.substring(6, 8));
      const hour = parseInt(creationHash.substring(8, 10));
      const minute = parseInt(creationHash.substring(10, 12));
      const second = parseInt(creationHash.substring(12, 14));
      const date = new Date(Date.UTC(year, month, day, hour, minute, second));

      let transformedUrl: string | undefined;
      let originalUrl: string | undefined;
      let styleUrl: string | undefined;

      let items: StorageReference[] = [];
      try {
          const folderRef = ref(storage, `users/${userUid}/${creationHash}`);
          const folderRes = await listAll(folderRef);
          items = folderRes.items;
      } catch (e) {
          console.warn(`Failed to list items for creation ${creationHash}`, e);
          return null;
      }

      const findItem = (pattern: RegExp) => items.find(i => i.name.match(pattern));

      const originalItem = findItem(/^original\./i);
      const transformedItem = findItem(/^transformed\./i);
      const styleItemLegacy = findItem(/^style\./i);

      if (originalItem) {
           try { originalUrl = await getDownloadURL(originalItem); } catch(e) {}
      }
      
      if (transformedItem) {
           try { transformedUrl = await getDownloadURL(transformedItem); } catch(e) {}
      }

      try {
          const styleDocRef = doc(db, 'users', userUid, 'creations', creationHash, 'styleImages', '0');
          const styleDoc = await getDoc(styleDocRef);
          
          if (styleDoc.exists()) {
              const data = styleDoc.data();
              if (data && data.genre && data.fileName) {
                  try {
                      const genreRef = ref(storage, `bibliotheque/${data.genre}`);
                      const genreRes = await listAll(genreRef);
                      
                      const exactMatch = genreRes.items.find(i => i.name === data.fileName);
                      let sharedItem = exactMatch;
                      
                      if (!sharedItem) {
                           const normalizedName = data.fileName.normalize('NFC');
                           sharedItem = genreRes.items.find(i => i.name.normalize('NFC') === normalizedName);
                      }
                      
                      if (sharedItem) {
                          styleUrl = await getDownloadURL(sharedItem);
                      } else {
                          styleUrl = await getDownloadURL(ref(storage, `bibliotheque/${data.genre}/${data.fileName}`));
                      }
                  } catch (e) {
                       styleUrl = await getDownloadURL(ref(storage, `bibliotheque/${data.genre}/${data.fileName}`));
                  }
              }
          }
      } catch (e) {
      }
      
      if (!styleUrl && styleItemLegacy) {
          try { styleUrl = await getDownloadURL(styleItemLegacy); } catch(e) {}
      }

      return {
          id: creationHash,
          date: date,
          originalImageUrl: originalUrl,
          styleImageUrl: styleUrl,
          transformedImageUrl: transformedUrl,
          status: transformedUrl ? 'completed' : 'in_progress',
          products: [] 
      } as Creation;
  };

  const loadBatch = async (ids: string[], startIndex: number) => {
    if (!user) return;
    const batchIds = ids.slice(startIndex, startIndex + BATCH_SIZE);
    if (batchIds.length === 0) return;
    
    if (startIndex > 0) setLoadingMore(true);

    try {
      const creationPromises = batchIds.map(id => fetchCreationDetails(id, user.uid));
      const results = await Promise.all(creationPromises);
      const fetchedCreations = results.filter((c): c is Creation => c !== null);
      
      setCreations(prev => {
        // Avoid duplicates if any
        const existingIds = new Set(prev.map(c => c.id));
        const newUnique = fetchedCreations.filter(c => !existingIds.has(c.id));
        return [...prev, ...newUnique];
      });
      setLoadedCount(prev => prev + batchIds.length);
    } catch (error) {
      console.error("Error loading batch:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const fetchCreations = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const userFolderRef = ref(storage, `users/${user.uid}`);
      const res = await listAll(userFolderRef);
      
      // Sort newest first
      const sortedIds = res.prefixes
        .map(p => p.name)
        .sort((a, b) => b.localeCompare(a)); // Descending string sort for YYYYMMDD... works for date

      setAllCreationIds(sortedIds);
      setCreations([]);
      setLoadedCount(0);

      // Load first batch
      await loadBatch(sortedIds, 0);

    } catch (error) {
      console.error("Error fetching creations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
     if (loadedCount < allCreationIds.length && !loadingMore) {
        loadBatch(allCreationIds, loadedCount);
     }
  };

  const handleNewCreation = () => {
    // debugger; 
    console.log("handleNewCreation called");
    setSelectedCreationId(null);
    setCurrentCreationId(null);
    setCurrentStage(1);
    setUploadedImagePreview(null);
    setCurrentTransformedImage(null);
    setCurrentProducts([]);
    setIsGeneratingProducts(false);
    setSelectedStyle(null);
    setIsBrowsingStyles(false);
    setIsTransforming(false);
    setSelectedProduct(null);
    setIsDetailOpen(false);
    setLoadingSecondaryTemplates([]);
    setLongGenerationModalOpen(false);
  };


  const generateCreationHash = (): string => {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  };

  const saveGeneratedProducts = async (products: GeneratedProduct[], creationHash: string) => {
    if (!user) return;
    
    const savePromises = products.map(async (product) => {
      try {
        const response = await fetch(product.previewUrl);
        const blob = await response.blob();
        
        const imageRef = ref(storage, `users/${user.uid}/${creationHash}/products/${product.id}/preview.jpg`);
        await uploadBytes(imageRef, blob);
        
        const productDocRef = doc(db, 'users', user.uid, 'creations', creationHash, 'products', product.id);
        await setDoc(productDocRef, product);
        
      } catch (error) {
        console.error(`Failed to save product ${product.id}`, error);
      }
    });

    await Promise.all(savePromises);
  };

  const startPollingProducts = (creationHash: string, showModal: boolean = true) => {
    if (!user || !templateConfig) return;
    
    if (pollingUnsubscribeRef.current) return;

    if (showModal) {
        setLongGenerationModalOpen(true);
    }
    
    // Start polling/listening to Firestore
    const productsColRef = collection(db, 'users', user.uid, 'creations', creationHash, 'products');
    const unsubscribe = onSnapshot(productsColRef, (snapshot) => {
        const fetchedProducts: GeneratedProduct[] = [];
        snapshot.forEach((doc) => {
            const data = doc.data() as GeneratedProduct;
            fetchedProducts.push(data);
        });

        if (fetchedProducts.length > 0) {
            setCurrentProducts(prev => {
                const existingIds = new Set(prev.map(p => p.id));
                const newOnes = fetchedProducts.filter(p => !existingIds.has(p.id));
                
                const allProducts = [...prev, ...newOnes];
                const receivedTemplateIds = new Set(allProducts.map(p => p.templateId));
                // We only care about secondary templates for the "Loading..." indicator
                // But if first batch failed, we might want to track that too?
                // The current loadingSecondaryTemplates logic focuses on secondPriority.
                // If first batch is missing, it will just appear when ready.
                
                if (templateConfig?.secondPriority) {
                     const remaining = templateConfig.secondPriority.filter(tid => !receivedTemplateIds.has(tid));
                     setLoadingSecondaryTemplates(remaining);
                     
                     if (remaining.length === 0) {
                        // We can't easily unsubscribe here inside the callback without a ref or variable
                        // But we can let the timeout handle it or use a mutable ref if this was a class/hook
                        // For now, let's just update the state.
                     }
                }
                
                // Stop spinner if we have something
                if (allProducts.length > 0) {
                    setIsGeneratingProducts(false);
                }

                return allProducts;
            });
        }
    });
    
    pollingUnsubscribeRef.current = unsubscribe;

    // Clear any existing timeout before setting a new one
    if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
    }

    // Safety timeout to stop listening after 20 minutes
    pollingTimeoutRef.current = setTimeout(() => {
        if (pollingUnsubscribeRef.current) {
            pollingUnsubscribeRef.current();
            pollingUnsubscribeRef.current = null;
        }
        setLoadingSecondaryTemplates([]);
        setIsGeneratingProducts(false);
        pollingTimeoutRef.current = null;
    }, 20 * 60 * 1000);
  };

  const handleTransform = async () => {
    console.log("handleTransform called", { uploadedImagePreview, selectedStyle, selectedGenre, user });
    if (!uploadedImagePreview || !selectedStyle || !selectedGenre || !user) return;

    // Consume credit
    try {
      await consumeCredit();
    } catch (error: any) {
      if (error.code === 'insufficient_balance') {
        setShowBuyCreditsModal(true);
        return;
      }
      console.error("Credit consumption failed:", error);
      alert("Failed to verify credits. Please try again.");
      return;
    }

    setIsTransforming(true);

    const contentBase64 = uploadedImagePreview;

    // Use URL directly if available to save bandwidth/time
    const styleUrl = selectedStyle.url;
    
    const otherStyles = selectedGenre.styles
        .filter(s => s.name !== selectedStyle.name)
        .slice(0, 3);
        
    const otherStyleUrls = otherStyles.map(s => s.url);
    
    let result;
    try {
        result = await transformImage(
            contentBase64, 
            "", // No base64 for main style
            [], // No base64 list
            selectedGenre.name,
            styleUrl,
            otherStyleUrls
        );
    } catch (error: any) {
        console.error("Transform API error:", error);
        const msg = error?.response?.data?.error || error.message || "Unknown error from Transform API";
        alert(`Transformation failed at API step: ${msg}`);
        setIsTransforming(false);
        return;
    }

    if (!result.image_base64) {
        console.error("No image returned from backend", result);
        alert("Transformation failed. Backend returned no image data.");
        setIsTransforming(false);
        return;
    }

    const transformedImage = `data:image/jpeg;base64,${result.image_base64}`;
    setCurrentTransformedImage(transformedImage);
    
    const creationHash = generateCreationHash();
    setCurrentCreationId(creationHash);
    
    setCurrentStage(3);
    setIsTransforming(false);
    
    setIsGeneratingProducts(true);
    let downloadUrl;
    try {
        const storageRef = ref(storage, `users/${user.uid}/${creationHash}/transformed.jpg`);
        const originalRef = ref(storage, `users/${user.uid}/${creationHash}/original.jpg`);
        
        const stylesToSave = [selectedStyle, ...otherStyles];
        
        const uploads: Promise<any>[] = [
            uploadString(storageRef, result.image_base64, 'base64', { contentType: 'image/jpeg' }),
            uploadString(originalRef, contentBase64, 'data_url', { contentType: 'image/jpeg' })
        ];

        stylesToSave.forEach((style, index) => {
             const styleDocRef = doc(db, 'users', user.uid, 'creations', creationHash, 'styleImages', String(index));
             uploads.push(setDoc(styleDocRef, {
                genre: selectedGenre.name,
                fileName: style.name
            }));
        });

        await Promise.all(uploads);
        
        downloadUrl = await getDownloadURL(storageRef);
    } catch (uploadError: any) {
        console.error("Upload error:", uploadError);
        alert("Failed to save transformed image to cloud. Product generation will be skipped.");
        setIsGeneratingProducts(false);
        return;
    }

    try {
        if (!templateConfig) {
            console.error("Templates not loaded yet");
            throw new Error("Templates configuration not loaded. Please refresh the page.");
        }
        
        let firstBatch: GeneratedProduct[] = [];
        let pollingStarted = false;

        try {
             // 1. Generate First Priority Products
            firstBatch = await generateProducts(
                downloadUrl, 
                creationHash, 
                templateConfig.firstPriority, 
                templateConfig.secondPriority
            );
        } catch (err: any) {
             if (err.isProcessing) {
                 startPollingProducts(creationHash);
                 pollingStarted = true;
             } else {
                 throw err;
             }
        }
        
        if (!pollingStarted && firstBatch.length > 0) {
            setCurrentProducts(firstBatch);
            saveGeneratedProducts(firstBatch, creationHash);
            
            // Allow user to see first batch while second batch loads
            setIsGeneratingProducts(false);

            // 2. Generate Second Priority Products
            if (templateConfig.secondPriority && templateConfig.secondPriority.length > 0) {
                setLoadingSecondaryTemplates(templateConfig.secondPriority);
                try {
                    const secondBatch = await generateProducts(
                        downloadUrl, 
                        creationHash, 
                        templateConfig.secondPriority, 
                        [],
                        'US',
                        'USD',
                        600
                    );
                    
                    if (secondBatch.length > 0) {
                        setCurrentProducts(prev => [...prev, ...secondBatch]);
                        saveGeneratedProducts(secondBatch, creationHash);
                    }
                    setLoadingSecondaryTemplates([]);
                } catch (secondError: any) {
                    // Fallback to polling for any error (e.g. timeout), so we don't lose the UI state
                    // and can pick up results if they eventually appear.
                    console.warn("Second batch generation error (falling back to polling):", secondError);
                    startPollingProducts(creationHash);
                }
            }
        } else if (!pollingStarted) {
             setIsGeneratingProducts(false);
        }
    } catch (productError: any) {
        console.error("Product generation error:", productError);
        setIsGeneratingProducts(false);
    }
  };

  const handleRegenerateProducts = async () => {
    const creationId = selectedCreationId || currentCreationId;
    if (!creationId) {
        alert("Cannot regenerate: No active creation found.");
        return;
    }
    if (!user || !templateConfig) return;
    
    // Check if we have the image URL
    let imageUrl = selectedCreation?.transformedImageUrl || currentTransformedImage;
    
    // If no image URL but we have creationId, try to fetch from Firebase storage
    if (!imageUrl) {
         try {
             // Try transformed first
             imageUrl = await getDownloadURL(ref(storage, `users/${user.uid}/${creationId}/transformed.jpg`));
         } catch (e) {
             // If not, maybe we can't regenerate easily without re-transforming, but let's try
         }
    }
    
    if (!imageUrl) {
        alert("Cannot regenerate products: Transformed image not found.");
        return;
    }

    // Stop any existing polling/timeouts to ensure a fresh start
    if (pollingUnsubscribeRef.current) {
        pollingUnsubscribeRef.current();
        pollingUnsubscribeRef.current = null;
    }
    if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
    }

    setIsGeneratingProducts(true);
    setLoadingSecondaryTemplates([]);

    try {
        let firstBatch: GeneratedProduct[] = [];
        let pollingStarted = false;

        try {
            firstBatch = await generateProducts(
                imageUrl, 
                creationId, 
                templateConfig.firstPriority, 
                templateConfig.secondPriority
            );
        } catch (err: any) {
             if (err.isProcessing) {
                 startPollingProducts(creationId);
                 pollingStarted = true;
             } else {
                 throw err;
             }
        }
        
        if (!pollingStarted && firstBatch.length > 0) {
            setCurrentProducts(firstBatch);
            saveGeneratedProducts(firstBatch, creationId);
            
            setIsGeneratingProducts(false);

            // Generate Second Priority
            if (templateConfig.secondPriority && templateConfig.secondPriority.length > 0) {
                setLoadingSecondaryTemplates(templateConfig.secondPriority);
                try {
                    const secondBatch = await generateProducts(
                        imageUrl, 
                        creationId, 
                        templateConfig.secondPriority, 
                        [],
                        'US',
                        'USD',
                        600
                    );
                    
                    if (secondBatch.length > 0) {
                        setCurrentProducts(prev => [...prev, ...secondBatch]);
                        saveGeneratedProducts(secondBatch, creationId);
                    }
                    setLoadingSecondaryTemplates([]);
                } catch (secondError: any) {
                    console.warn("Second batch generation error (falling back to polling):", secondError);
                    startPollingProducts(creationId);
                }
            }
        } else if (!pollingStarted) {
             setIsGeneratingProducts(false);
        }
    } catch (productError: any) {
        console.error("Product regeneration error:", productError);
        alert("Failed to regenerate products. Please try again.");
        setIsGeneratingProducts(false);
    }
  };

  const fetchStyles = async () => {
    if (genres.length > 0) return;
    setLoadingStyles(true);
    try {
      const rootRef = ref(storage, 'bibliotheque');
      const res = await listAll(rootRef);
      const loadedGenres: Genre[] = [];

      for (const folderRef of res.prefixes) {
        const genreName = folderRef.name;
        const styleRes = await listAll(folderRef);
        
        const stylePromises = styleRes.items
            .filter(item => item.name.match(/\.(jpg|jpeg|png)$/i))
            .map(async (itemRef) => {
                const url = await getDownloadURL(itemRef);
                return {
                    name: itemRef.name,
                    url: url,
                    path: itemRef.fullPath
                };
            });
        
        const loadedStyles = await Promise.all(stylePromises);
        if (loadedStyles.length > 0) {
            loadedGenres.push({
                name: genreName,
                styles: loadedStyles
            });
        }
      }
      
      loadedGenres.sort((a, b) => a.name.localeCompare(b.name));
      
      setGenres(loadedGenres);
      if (loadedGenres.length > 0) {
        setSelectedGenre(loadedGenres[0]);
      }
    } catch (error) {
      console.error("Error fetching styles:", error);
    } finally {
      setLoadingStyles(false);
    }
  };

  const handleBrowseStyles = () => {
    setIsBrowsingStyles(true);
    fetchStyles();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const loadCreationProducts = async (creationId: string) => {
    debugger;
    if (!user) return;
    
    setFetchedCreationIds(prev => new Set(prev).add(creationId));
    
    setLoadingProducts(true);
    let currentLoadedProducts: Product[] = [];
    
    try {
        const productsColRef = collection(db, 'users', user.uid, 'creations', creationId, 'products');
        const snapshot = await getDocs(productsColRef);
        
        const loadedProducts: Product[] = [];

        await Promise.all(snapshot.docs.map(async (docSnap) => {
            const data = docSnap.data() as GeneratedProduct;
            const productId = docSnap.id;
            let previewUrl = data.previewUrl;

            try {
                previewUrl = await getDownloadURL(ref(storage, `users/${user.uid}/${creationId}/products/${productId}/preview.jpg`));
            } catch (e) {
                // Ignore, fallback to data.previewUrl
                console.warn(`Failed to load storage image for product ${productId}`, e);
            }
            
            // Filter out invalid preview URLs (e.g. relative paths that cause 404s)
            if (!previewUrl || (typeof previewUrl === 'string' && previewUrl.startsWith('users/'))) {
                 return;
            }

            let priceDisplay = '$ --';
            let fetchedPrice = null;

            // Always try to show USD
            if (data.productPrice && data.productPrice.currency === 'USD') {
                priceDisplay = `${data.productPrice.currency} ${data.productPrice.price}`;
            } else if (data.productUid) {
                try {
                    // Force USD
                    const priceData = await fetchProductPrice(data.productUid, 'US', 'USD');
                    priceDisplay = `${priceData.currency} ${priceData.unitPrice}`;
                    fetchedPrice = {
                        price: priceData.unitPrice,
                        currency: priceData.currency,
                        quantity: 1,
                        productUid: data.productUid
                    };
                } catch (err) {
                    console.warn("Failed to fetch price", err);
                    // Fallback to stored price if available
                    if (data.productPrice) {
                        priceDisplay = `${data.productPrice.currency} ${data.productPrice.price}`;
                    }
                }
            }

            if (previewUrl) {
                loadedProducts.push({
                    id: productId,
                    name: data.title || `Product ${productId.substring(0, 5)}`,
                    type: 'Custom Product',
                    price: priceDisplay,
                    image: previewUrl,
                    gelatoProduct: {
                         ...data,
                         previewUrl: previewUrl,
                         productPrice: fetchedPrice || data.productPrice
                    } as GelatoProduct 
                });
            }
        }));
        
        setCreations(prev => prev.map(c => c.id === creationId ? { ...c, products: loadedProducts } : c));
        
        // Also update currentProducts if this is the current creation (important for New Creation flow or recovering state)
        if (creationId === currentCreationId) {
             const generatedProducts = loadedProducts
                .map(p => p.gelatoProduct)
                .filter((p): p is GelatoProduct => !!p) as unknown as GeneratedProduct[];
             setCurrentProducts(generatedProducts);
        }

        currentLoadedProducts = loadedProducts;

    } catch (error) {
        console.error("Error loading products", error);
    } finally {
        setLoadingProducts(false);
    }

    // Explicit check for old creations with no products: don't auto-poll, allow manual regenerate
    if (currentLoadedProducts.length === 0) {
         const timeoutHours = templateConfig?.productGenerationTimeoutHours || 3;
         let isTimedOut = false;
         try {
             // Parse YYYYMMDDHHmmSS
             const year = parseInt(creationId.substring(0, 4));
             const month = parseInt(creationId.substring(4, 6)) - 1; 
             const day = parseInt(creationId.substring(6, 8));
             const hour = parseInt(creationId.substring(8, 10));
             const minute = parseInt(creationId.substring(10, 12));
             const second = parseInt(creationId.substring(12, 14));
             const creationDate = new Date(year, month, day, hour, minute, second);
             
             const diffMs = new Date().getTime() - creationDate.getTime();
             const diffHours = diffMs / (1000 * 60 * 60);
             
             if (diffHours > timeoutHours) {
                 isTimedOut = true;
             }
         } catch (e) {
             console.warn("Failed to parse creation date for timeout check", e);
         }

         if (isTimedOut) {
             console.log(`Creation ${creationId} is older than ${timeoutHours} hours and has no products. Enabling regenerate button.`);
             setIsGeneratingProducts(false);
             setLoadingSecondaryTemplates([]);
             return; 
         }
    }

    if (templateConfig && templateConfig.secondPriority) {
        const loadedTemplateIds = new Set<string>();
        currentLoadedProducts.forEach(p => {
             if (p.gelatoProduct?.templateId) {
                 loadedTemplateIds.add(p.gelatoProduct.templateId);
             } else if ('templateId' in p) {
                 // @ts-ignore
                 loadedTemplateIds.add(p.templateId);
             }
        });
        
        const missingSecondary = templateConfig.secondPriority.filter(tid => !loadedTemplateIds.has(tid));
        
        if (missingSecondary.length > 0) {
            // Check if creation is too old to poll
            const timeoutHours = templateConfig.productGenerationTimeoutHours || 3;
            let isTimedOut = false;
            
            try {
                const year = parseInt(creationId.substring(0, 4));
                const month = parseInt(creationId.substring(4, 6)) - 1; 
                const day = parseInt(creationId.substring(6, 8));
                const hour = parseInt(creationId.substring(8, 10));
                const minute = parseInt(creationId.substring(10, 12));
                const second = parseInt(creationId.substring(12, 14));
                const creationDate = new Date(year, month, day, hour, minute, second);
                
                const diffMs = new Date().getTime() - creationDate.getTime();
                const diffHours = diffMs / (1000 * 60 * 60);
                
                if (diffHours > timeoutHours) {
                    isTimedOut = true;
                }
            } catch (e) {
                console.warn("Failed to parse creation date for timeout check", e);
            }

            if (isTimedOut) {
                console.log(`Creation ${creationId} is older than ${timeoutHours} hours. Skipping auto-polling for missing products.`);
                setLoadingSecondaryTemplates([]);
                setIsGeneratingProducts(false);
            } else {
                console.log("Found missing secondary products, restarting polling...", missingSecondary);
                
                const initialForPolling = currentLoadedProducts
                    .map(p => p.gelatoProduct)
                    .filter((p): p is GelatoProduct => !!p)
                    .map(p => p as unknown as GeneratedProduct);
                    
                setCurrentProducts(initialForPolling);
                setLoadingSecondaryTemplates(missingSecondary);
                setIsGeneratingProducts(true);
                
                startPollingProducts(creationId, false);
            }
        }
    }
  };

  const handleSelectCreation = async (creationId: string) => {
    setSelectedCreationId(creationId);
    setCurrentCreationId(creationId);
    setMobileSidebarOpen(false);
    setSelectedProduct(null);
    setIsDetailOpen(false);
    
    // Reset generation state to ensure clean slate
    setCurrentProducts([]);
    setLoadingSecondaryTemplates([]);
    setIsGeneratingProducts(false);
    
    // Stop any existing polling
    if (pollingUnsubscribeRef.current) {
        pollingUnsubscribeRef.current();
        pollingUnsubscribeRef.current = null;
    }
    if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
    }

    const creation = creations.find(c => c.id === creationId);
    
    if (creation && creation.products.length === 0 && !fetchedCreationIds.has(creationId)) {
        await loadCreationProducts(creationId);
    }
    // Note: If creation has products, we assume polling logic is handled or done.
    // However, if we want to ensure "Loading..." items appear even if main products are there, 
    // we might need to invoke the polling check part of loadCreationProducts or separate it.
    // Based on original code, the polling check ran even if products were just loaded.
    // So let's double check if we need to call loadCreationProducts even if length > 0 but it's not "complete".
    // But for now, if it's already in memory (length > 0), we assume it's fine.
    // The main issue was returning from Order where length is 0.

    setCurrentStage(3);
  };

  const handleCheckout = () => {
    if (!selectedProduct) return;
    
    let productToPass = selectedProduct;

    // If it's a history product (has gelatoProduct), extract the inner object which matches the API structure
    if ('gelatoProduct' in selectedProduct && selectedProduct.gelatoProduct) {
        // Cast to unknown first because TypeScript might complain about incompatible types
        // even though runtime data is compatible for what Order page needs
        productToPass = selectedProduct.gelatoProduct as unknown as GeneratedProduct;
    }

    const productName = 'title' in productToPass ? (productToPass as GeneratedProduct).title : (productToPass as any).name;
    console.log("Proceeding to checkout for:", productName);
    
    const creationId = selectedCreationId || currentCreationId;

    // Update current history entry to save state for "Back" navigation
    navigate(location.pathname, { 
        replace: true, 
        state: { 
            creationId: creationId,
            restoreStage: currentStage
        }
    });

    setTimeout(() => {
        navigate('/order', { 
            state: { 
                product: productToPass,
                creationId: creationId
            } 
        });
    }, 10);
  };

  const selectedCreation = creations.find(c => c.id === selectedCreationId);

  // Derived state for StageIndicator
  const isHistoryMode = !!selectedCreationId;
  const firstProductPreview = currentProducts.length > 0 
      ? currentProducts.find(p => p.title?.toLowerCase().includes('canvas'))?.previewUrl || currentProducts[0]?.previewUrl
      : null;

  const stageImages = {
      1: isHistoryMode ? selectedCreation?.originalImageUrl : uploadedImagePreview,
      2: isHistoryMode ? selectedCreation?.styleImageUrl : selectedStyle?.url,
      3: isHistoryMode ? selectedCreation?.transformedImageUrl : currentTransformedImage,
      4: isHistoryMode ? (selectedCreation?.products && selectedCreation.products.length > 0 ? (selectedCreation.products.find(p => p.name?.toLowerCase().includes('canvas')) || selectedCreation.products[0]).image : null) : firstProductPreview,
      5: latestOrderPreview
  };

  return (
    <div className="flex h-[calc(100vh-64px)] bg-gray-50 relative"> 
      <SEO 
        title="Create Your Masterpiece"
        description="Upload your photo, choose a style, and create stunning custom products with AI art."
        url="https://www.atelier-persopol.com/create"
      />
      <CreationSidebar 
        mobileSidebarOpen={mobileSidebarOpen}
        setMobileSidebarOpen={setMobileSidebarOpen}
        onNewCreation={handleNewCreation}
        loading={loading}
        creations={creations}
        selectedCreationId={selectedCreationId}
        onSelectCreation={handleSelectCreation}
        onRefresh={fetchCreations}
        hasMore={loadedCount < allCreationIds.length}
        onLoadMore={handleLoadMore}
        loadingMore={loadingMore}
      />

      <main className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileSidebarOpen(true)}
                className="md:hidden p-2 hover:bg-gray-200 rounded-lg transition-colors"
                aria-label="Open sidebar"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-2xl md:text-3xl font-serif font-bold text-gray-900">
              {selectedCreation 
                ? `Creation: ${selectedCreation.date.toLocaleDateString()}` 
                : currentTransformedImage 
                  ? "New Creation" 
                  : "Start Creating"}
              </h1>
            </div>
            {selectedCreation && (
              <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                {selectedCreation.status === 'completed' ? 'Completed' : 'In Progress'}
              </span>
            )}
            {!selectedCreation && currentTransformedImage && (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                Transformed
              </span>
            )}
          </div>
          
          <StageIndicator 
            stages={stages}
            currentStage={currentStage}
            isHistoryMode={isHistoryMode}
            stageImages={stageImages}
            onStageSelect={setCurrentStage}
          />

          <div className="bg-white rounded-xl md:rounded-2xl border border-gray-100 shadow-sm p-4 md:p-6 min-h-[300px] md:min-h-[400px]">
             {currentStage === 1 && (
                 <UploadImage 
                    uploadedImagePreview={uploadedImagePreview}
                    selectedCreation={selectedCreation}
                    isHistoryMode={isHistoryMode}
                    onFileChange={handleFileChange}
                    onClearImage={() => setUploadedImagePreview(null)}
                    onContinue={() => setCurrentStage(2)}
                 />
             )}

             {currentStage === 2 && (
                 <StyleSelect 
                    isBrowsingStyles={isBrowsingStyles}
                    selectedStyle={selectedStyle}
                    selectedCreation={selectedCreation}
                    isHistoryMode={isHistoryMode}
                    loadingStyles={loadingStyles}
                    isTransforming={isTransforming}
                    genres={genres}
                    selectedGenre={selectedGenre}
                    onBrowseStyles={handleBrowseStyles}
                    onTransform={handleTransform}
                    onSelectGenre={setSelectedGenre}
                    onSelectStyle={setSelectedStyle}
                 />
             )}

             {currentStage === 3 && (
                 <TransformationSuccess 
                    currentTransformedImage={currentTransformedImage}
                    selectedCreation={selectedCreation}
                    onViewProducts={() => setCurrentStage(4)}
                 />
             )}

             {currentStage === 4 && (
                 <CustomizedProducts 
                    selectedCreation={selectedCreation}
                    currentTransformedImage={currentTransformedImage}
                    loadingProducts={loadingProducts}
                    isGeneratingProducts={isGeneratingProducts}
                    currentProducts={currentProducts}
                    selectedProduct={selectedProduct}
                    isDetailOpen={isDetailOpen}
                    setIsDetailOpen={setIsDetailOpen}
                    setSelectedProduct={setSelectedProduct}
                    onCheckout={handleCheckout}
                    templateConfig={templateConfig}
                    loadingSecondaryTemplates={loadingSecondaryTemplates}
                    onRegenerate={handleRegenerateProducts}
                 />
             )}

             {currentStage === 5 && (
                <OrderDetails selectedCreation={selectedCreation} loadingProducts={loadingProducts} />
             )}
          </div>
        </div>
      </main>

      {/* Long Generation Warning Modal */}
      {longGenerationModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-white/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 relative animate-in fade-in zoom-in duration-200 border border-gray-100">
            <button 
              onClick={() => setLongGenerationModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">High Traffic</h2>
              <p className="text-gray-500 mt-2">
                The product generation is taking longer than usual due to high traffic. Please be patient.
              </p>
            </div>

            <button
              onClick={() => setLongGenerationModalOpen(false)}
              className="w-full bg-black text-white py-3 rounded-xl font-medium hover:bg-gray-800 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      <BuyCreditsModal 
        isOpen={showBuyCreditsModal} 
        onClose={() => setShowBuyCreditsModal(false)} 
      />
    </div>
  );
};

